import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AssistantDisplayMode = 'floating' | 'side' | 'bottom';

export interface AssistantLayoutState {
  displayMode: AssistantDisplayMode;
  isOpen: boolean;
  sidePanelWidth: number;
  bottomPanelHeight: number;
}

/** Keeps the selected assistant conversation while the current SPA session is alive. */
@Injectable({ providedIn: 'root' })
export class AssistantUiStateService {
  private static readonly sidePanelMinimum = 352;
  private static readonly sidePanelMaximum = 672;
  private static readonly bottomPanelMinimum = 240;
  private static readonly bottomPanelMaximum = 672;
  private static readonly panelSizeStorageKey = 'bizzweaver-assistant-panel-size';
  private readonly activeConversationByUser = new Map<string, string | null>();
  private readonly layoutStateSubject = new BehaviorSubject<AssistantLayoutState>(this.restoreLayoutState());
  readonly layoutState$ = this.layoutStateSubject.asObservable();

  get layoutState(): AssistantLayoutState {
    return this.layoutStateSubject.value;
  }

  getActiveConversation(userPublicId: string): string | null {
    return this.activeConversationByUser.get(userPublicId) ?? null;
  }

  setActiveConversation(userPublicId: string, conversationId: string | null): void {
    this.activeConversationByUser.set(userPublicId, conversationId);
  }

  clearUser(userPublicId: string): void {
    this.activeConversationByUser.delete(userPublicId);
  }

  setDisplayMode(displayMode: AssistantDisplayMode): void {
    this.updateLayoutState({ displayMode });
  }

  setOpen(isOpen: boolean): void {
    this.layoutStateSubject.next({ ...this.layoutState, isOpen });
  }

  setSidePanelWidth(width: number): void {
    const sidePanelWidth = this.clamp(width, AssistantUiStateService.sidePanelMinimum, AssistantUiStateService.sidePanelMaximum);
    this.updateLayoutState({ sidePanelWidth });
  }

  setBottomPanelHeight(height: number): void {
    const bottomPanelHeight = this.clamp(height, AssistantUiStateService.bottomPanelMinimum, AssistantUiStateService.bottomPanelMaximum);
    this.updateLayoutState({ bottomPanelHeight });
  }

  private updateLayoutState(update: Partial<Pick<AssistantLayoutState, 'displayMode' | 'sidePanelWidth' | 'bottomPanelHeight'>>): void {
    const next = { ...this.layoutState, ...update };
    this.layoutStateSubject.next(next);
    this.persistLayoutState(next);
  }

  private restoreLayoutState(): AssistantLayoutState {
    const defaults: AssistantLayoutState = { displayMode: 'floating', isOpen: false, sidePanelWidth: 448, bottomPanelHeight: 352 };
    if (typeof localStorage === 'undefined') return defaults;
    try {
      const raw = localStorage.getItem(AssistantUiStateService.panelSizeStorageKey);
      const stored = raw ? JSON.parse(raw) as Record<string, unknown> : {};
      return {
        displayMode: stored['displayMode'] === 'side' || stored['displayMode'] === 'bottom' ? stored['displayMode'] : 'floating',
        isOpen: false,
        sidePanelWidth: this.clamp(Number(stored['sidePanelWidth']), AssistantUiStateService.sidePanelMinimum, AssistantUiStateService.sidePanelMaximum, defaults.sidePanelWidth),
        bottomPanelHeight: this.clamp(Number(stored['bottomPanelHeight']), AssistantUiStateService.bottomPanelMinimum, AssistantUiStateService.bottomPanelMaximum, defaults.bottomPanelHeight)
      };
    } catch {
      return defaults;
    }
  }

  private persistLayoutState(layout: AssistantLayoutState): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(AssistantUiStateService.panelSizeStorageKey, JSON.stringify({
        displayMode: layout.displayMode,
        sidePanelWidth: layout.sidePanelWidth,
        bottomPanelHeight: layout.bottomPanelHeight
      }));
    } catch {
      // Panel sizing is a convenience preference and must never block the assistant.
    }
  }

  private clamp(value: number, minimum: number, maximum: number, fallback = minimum): number {
    return Number.isFinite(value) ? Math.round(Math.min(maximum, Math.max(minimum, value))) : fallback;
  }
}
